import { useEffect, useMemo, useState } from "react";
import { useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { keccak256, toBytes } from "viem";
import { useT } from "../lib/i18n";
import type { RedKeel } from "../config/networks";
import type { EstadoVault } from "../hooks/useVault";
import type { Acc } from "../hooks/useAcciones";
import { twapDe, type Entrada, type Plan } from "../lib/achicar";
import { gatillo, planAhora, siCae, subidaHasta, type Posicion } from "../lib/redistribuir";
import { CSS_ACHICAR } from "./ModalAchicar";

const POOL_ABI = [
  { type: "function", name: "slot0", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }] },
  { type: "function", name: "observe", stateMutability: "view", inputs: [{ type: "uint32[]" }],
    outputs: [{ type: "int56[]" }, { type: "uint160[]" }] },
] as const;
const VAULT_FEE_ABI = [
  { type: "function", name: "fee", stateMutability: "view", inputs: [], outputs: [{ type: "uint24" }] },
] as const;

/** Amplitudes objetivo de la tabla de gatillos (las de la herramienta de referencia). */
const PRESETS = [0.25, 0.20, 0.15, 0.10, 0.08, 0.06, 0.05];
/** Slot de la orden programada en el vault (`KeelRebalancer.ORDEN_SLOT`): below | above << 24 | 1 << 48. */
const ORDEN_SLOT = keccak256(toBytes("keel.rebalancer.orden"));
/** Caída con la que se enseña el costo de redistribuir. */
const CAIDA = 5;

/**
 * REDISTRIBUCIÓN SIN APORTE. Botón y pantalla APARTE de «Achicar rango»: aquí no se pone dinero.
 * Cuando el precio subió dentro del rango, el estable que la piscina acumuló compra el activo y el
 * rango se estrecha sin dejar de salir con el capital. Se enseña cuándo compensa (precio gatillo
 * por amplitud) y, con la misma importancia, lo que cuesta si el activo vuelve a caer.
 */
export function ModalRedistribuir({ v, red, acciones, cerrar }: {
  v: EstadoVault; red: RedKeel; acciones: Acc; cerrar: () => void;
}) {
  const t = useT();
  const config = useConfig();
  const estable = v.refEsToken0 ? v.token0 : v.token1;
  const activo = v.refEsToken0 ? v.token1 : v.token0;
  const dRef = estable.decimals;

  const [cadena, setCadena] = useState<{ sqrtP: number; tick: number; twap: number; fee: number } | null>(null);
  const [error, setError] = useState("");
  const [orden, setOrden] = useState<{ below: number; above: number } | null>(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const publico = getPublicClient(config, { chainId: red.idCadena });
        if (!publico) throw new Error("sin RPC");
        const [slot0, obs, fee] = await publico.multicall({
          allowFailure: false,
          contracts: [
            { address: v.pool, abi: POOL_ABI, functionName: "slot0" },
            { address: v.pool, abi: POOL_ABI, functionName: "observe", args: [[v.twapWindow, 0]] },
            { address: v.direccion, abi: VAULT_FEE_ABI, functionName: "fee" },
          ],
        });
        if (!vivo) return;
        // La orden programada vive en un slot propio: sin getter, se lee el almacenamiento.
        try {
          const o = BigInt((await publico.getStorageAt({ address: v.direccion, slot: ORDEN_SLOT })) ?? "0x0");
          if (vivo) setOrden(o > 0n ? { below: Number(o & 0xffffffn), above: Number((o >> 24n) & 0xffffffn) } : null);
        } catch { /* sin orden legible: se trata como sin orden */ }
        setCadena({
          sqrtP: Number(slot0[0]) / 2 ** 96, tick: Number(slot0[1]),
          twap: twapDe(BigInt(obs[0][0] ?? 0n), BigInt(obs[0][1] ?? 0n), v.twapWindow),
          fee: Number(fee) / 1e6,
        });
      } catch (e) {
        if (vivo) setError(e instanceof Error ? e.message.split("\n")[0]! : String(e));
      }
    })();
    return () => { vivo = false; };
  }, [v.pool, v.direccion, v.twapWindow, red.idCadena]); // eslint-disable-line

  // Lo que retiene el vault y nunca se despliega (bolsa de gas, ganancia apartada, reserva).
  const ret0 = Number(v.gasBudget0) + Number(v.profit0) + (v.refEsToken0 ? Number(v.reserva) : 0);
  const ret1 = Number(v.gasBudget1) + Number(v.profit1) + (!v.refEsToken0 ? Number(v.reserva) : 0);

  const entrada: Entrada | null = useMemo(() => {
    if (!cadena) return null;
    const precioCrudo = cadena.sqrtP * cadena.sqrtP;
    const have0 = Math.max(0, (v.enPosicion.a0 + v.libre.a0) * 10 ** v.token0.decimals - ret0);
    const have1 = Math.max(0, (v.enPosicion.a1 + v.libre.a1) * 10 ** v.token1.decimals - ret1);
    const est = (v.refEsToken0 ? have0 : have1) / 10 ** dRef;
    const act = v.refEsToken0 ? have1 / precioCrudo : have0 * precioCrudo;
    return {
      refEsToken0: v.refEsToken0, sqrtP: cadena.sqrtP, tick: cadena.tick, twap: cadena.twap,
      spacing: Math.max(1, v.spacing), fee: cadena.fee,
      D: est + act / 10 ** dRef, estable: est,
      T: Number(v.costBasisRef - v.reserva) / 10 ** dRef,
    };
  }, [cadena, v]); // eslint-disable-line

  const pos: Posicion | null = v.rango && v.liquidez > 0 ? {
    L: v.liquidez, lo: v.rango.lo, hi: v.rango.hi,
    libre0: v.libre.a0 * 10 ** v.token0.decimals - ret0,
    libre1: v.libre.a1 * 10 ** v.token1.decimals - ret1,
    dec0: v.token0.decimals, dec1: v.token1.decimals,
  } : null;

  const bajoElAgua = !!entrada && entrada.D < entrada.T;
  const logicaOk = (red.logicasRedistribuir ?? []).some((l) => l.toLowerCase() === v.rebalancer.toLowerCase());
  // Programar (esperar al gatillo y que lo ejecute el robot) solo con la lógica de 2026-10-30.
  const logicaOrden = (red.logicasOrden ?? []).some((l) => l.toLowerCase() === v.rebalancer.toLowerCase());
  const anchoHoy = v.rango ? Math.pow(1.0001, v.rango.hi - v.rango.lo) - 1 : 0;

  const [s, setS] = useState(0.03);
  const [elegido, setElegido] = useState<number | null>(null);

  const ahora = useMemo(() => (entrada && bajoElAgua ? planAhora(entrada, s, anchoHoy) : null), [entrada, bajoElAgua, s, anchoHoy]);
  const filas = useMemo(() => {
    if (!entrada || !pos || !bajoElAgua) return [];
    return PRESETS.filter((w) => w < anchoHoy * 0.97).map((w) => ({ w, g: gatillo(entrada, pos, w, s) }));
  }, [entrada, bajoElAgua, s, anchoHoy]); // eslint-disable-line

  // La fila elegida: si ya se paga sola se redistribuye AHORA; si su gatillo está por llegar, se
  // PROGRAMA (la ejecuta el robot al llegar). Sin elegir, el más estrecho de ahora.
  const filaSel = filas.find((f) => f.w === elegido && f.g);
  const futura = filaSel && filaSel.g!.tick !== entrada?.tick && logicaOrden ? filaSel : null;
  const plan: Plan | null = futura ? null
    : filaSel && filaSel.g!.tick === entrada?.tick ? filaSel.g!.plan : ahora;
  const costo = entrada && pos && plan ? siCae(entrada, pos, plan, CAIDA) : null;

  const pTick = (tk: number) => {
    const p = Math.pow(1.0001, tk) * 10 ** (v.token0.decimals - v.token1.decimals);
    return v.refEsToken0 ? 1 / p : p;
  };
  const fmtP = (p: number) => p.toLocaleString(undefined, { maximumSignificantDigits: 5 });
  const fmtN = (n: number, dec = 2) => n.toLocaleString(undefined, { maximumFractionDigits: dec });
  const techoHoy = v.rango ? (v.refEsToken0 ? v.rango.lo : v.rango.hi) : 0;

  // Igual que al achicar: tras la banda de recuperación se devuelve la forma de siempre, para que
  // al salir con el capital el robot re-centre con ella y no recompre el activo.
  const restaurar = v.ticksBelow > 0 && v.ticksAbove > 0
    ? { below: v.ticksBelow, above: v.ticksAbove }
    : { below: v.halfWidthTicks, above: v.halfWidthTicks };

  async function firmar() {
    if (futura) await acciones.programarRedistribucion(futura.g!.plan.below, futura.g!.plan.above, restaurar);
    else if (plan) await acciones.redistribuir(plan.below, plan.above, restaurar);
    else return;
    cerrar();
  }
  async function cancelarOrden() {
    await acciones.cancelarRedistribucion();
    cerrar();
  }
  // Gatillo aproximado de la orden guardada (su ancho, con el % de estable elegido).
  const gOrden = orden && entrada && pos ? gatillo(entrada, pos, Math.pow(1.0001, orden.below + orden.above) - 1, s) : null;

  const ocupado = !!acciones.haciendo;
  const cargando = !entrada && !error;

  const ancho = bajoElAgua && logicaOk && !!pos && !error && !cargando;

  return (
    <div className="ach-ov" onClick={cerrar}>
      <style>{CSS_ACHICAR}</style>
      <div className={"ach-box" + (ancho ? " wide" : "")} onClick={(e) => e.stopPropagation()}>
        <div className="ach-pad">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="ach-title"><span className="ach-ico">⇵</span>{t("Redistribuir sin aporte")}</div>
            <button className="ach-x" onClick={cerrar} aria-label="cerrar">✕</button>
          </div>

          {error ? <div className="ach-err">{error}</div>
            : cargando ? <div className="ach-card load"><div className="ach-big"><span style={{ color: "#6f6f7c" }}>·····</span></div></div>
            : !bajoElAgua || !pos ? (
              <p className="ach-sub">{t("Tu piscina no está por debajo de tu capital: no hace falta redistribuir. Para concentrar la liquidez usa «Achicar rango».")}</p>
            ) : !logicaOk ? (
              <>
                <p className="ach-sub">{t("Para redistribuir sin aporte tu piscina necesita la lógica nueva.")}</p>
                {v.hayActualizacion
                  ? <div className="ach-foot"><button className="ach-btn" onClick={cerrar}>{t("Cancelar")}</button>
                      <button className="ach-btn pri" disabled={ocupado} onClick={() => acciones.actualizarLogica()}>{t("Actualizar la lógica")}</button></div>
                  : <div className="ach-err">{t("Tu piscina es de una versión anterior de Keel y no puede recibir esta lógica. Para usarlo: retira tu piscina y créala de nuevo con «Rescatar una posición» indicando tu capital como objetivo.")}</div>}
              </>
            ) : (
              <div className="ach-cols"><div>
                <p className="ach-sub">
                  {t("Cuando {a} sube dentro de tu rango, la piscina acumula {s}. Ese {s} vuelve a comprar {a} y el rango se estrecha: al tocar el techo sigues saliendo con tu capital, sin poner nada.", { a: activo.symbol, s: estable.symbol })}
                </p>
                <div className="ach-row" style={{ marginTop: 10 }}><span className="ach-k">{t("Vale hoy / tu capital")}</span>
                  <span className="ach-v">{fmtN(entrada!.D)} / {fmtN(entrada!.T)} {estable.symbol}</span></div>
                <div className="ach-row"><span className="ach-k">{t("Techo de hoy")}</span>
                  <span className="ach-v">{fmtP(pTick(techoHoy))} (+{fmtN(subidaHasta(entrada!, techoHoy), 1)}%)</span></div>

                <div className="ach-lbl"><span>{t("% en {s} al abrir", { s: estable.symbol })}</span></div>
                <div className="ach-chips">
                  {[0.02, 0.03, 0.05].map((x) => (
                    <button key={x} className={"ach-chip" + (s === x ? " on" : "")} onClick={() => setS(x)}>{Math.round(x * 100)}%</button>
                  ))}
                </div>

                <div className="ach-lbl"><span>{t("Precios gatillo")}</span></div>
                <p className="ach-sub" style={{ marginTop: -4, fontSize: 12 }}>
                  {logicaOrden
                    ? t("A qué precio cada ancho se paga solo. Elige uno que aún no llega y queda programado: el robot lo ejecuta solo cuando {a} llegue a ese precio.", { a: activo.symbol })
                    : t("A qué precio cada ancho se paga solo. Cuanto más estrecho, más tiene que subir antes.")}
                </p>
                <div className="ach-gat">
                  {filas.map(({ w, g }) => {
                    const ya = !!g && g.tick === entrada!.tick;
                    const vale = ya || (!!g && logicaOrden);
                    const on = vale && (ya ? plan === g!.plan : futura?.w === w);
                    return (
                      <button key={w} className={"ach-chip" + (ya ? " ya" : "") + (on ? " on" : "")} disabled={!vale}
                        style={{ opacity: vale ? 1 : 0.6, cursor: vale ? "pointer" : "default" }}
                        onClick={() => vale && setElegido(w)}>
                        <span>{Math.round(w * 100)}%</span>
                        <small>{!g ? t("no llega") : ya ? t("✓ ya puedes")
                          : <>{fmtP(pTick(g.tick))} · +{fmtN(subidaHasta(entrada!, g.tick), 1)}%</>}</small>
                      </button>
                    );
                  })}
                </div>
              </div><div className="der">
                {orden && (
                  <div className="ach-card" style={{ marginBottom: 12, borderColor: "#3a3a46" }}>
                    <div className="ach-big" style={{ fontSize: 19 }}>{t("Redistribución programada")}<span className="ach-tag">{t("en espera")}</span></div>
                    <div className="ach-row"><span className="ach-k">{t("Ancho")}</span>
                      <span className="ach-v">{fmtN((Math.pow(1.0001, orden.below + orden.above) - 1) * 100, 1)}%</span></div>
                    {gOrden && gOrden.tick !== entrada!.tick && (
                      <div className="ach-row"><span className="ach-k">{t("Se ejecuta cuando {a} llegue a (aprox.)", { a: activo.symbol })}</span>
                        <span className="ach-v">{fmtP(pTick(gOrden.tick))} (+{fmtN(subidaHasta(entrada!, gOrden.tick), 1)}%)</span></div>
                    )}
                    <p className="ach-sub" style={{ fontSize: 12 }}>{t("Tu piscina no ha cambiado. El robot la redistribuye solo al llegar al gatillo, y el contrato vuelve a comprobar que sales con tu capital.")}</p>
                    <div className="ach-foot" style={{ marginTop: 10 }}>
                      <button className="ach-btn" disabled={ocupado} onClick={cancelarOrden}>
                        {acciones.haciendo === "cancelarRedistribucion" ? <><span className="ach-spin" /> {t("Cancelando…")}</> : t("Cancelar orden")}
                      </button>
                    </div>
                  </div>
                )}
                {futura ? (
                  <>
                    <div className="ach-card">
                      <div className="ach-big">{fmtN(futura.g!.plan.ancho * 100, 1)}%<span className="ach-unit">{t("de ancho")}</span>
                        <span className="ach-tag">{t("programada")}</span></div>
                      <div className="ach-row"><span className="ach-k">{t("Se ejecuta cuando {a} llegue a", { a: activo.symbol })}</span>
                        <span className="ach-v">{fmtP(pTick(futura.g!.tick))} (+{fmtN(subidaHasta(entrada!, futura.g!.tick), 1)}%)</span></div>
                      <div className="ach-row"><span className="ach-k">{t("Sales con (en el techo)")}</span>
                        <span className="ach-v ach-ok">{fmtN(entrada!.T)} {estable.symbol}</span></div>
                      <div style={{ fontSize: 11.5, color: "#6f6f7c", marginTop: -2 }}>{t("Tu capital. Lo asegura el contrato.")}</div>
                      <div className="ach-row"><span className="ach-k">{t("Techo nuevo (aprox.)")}</span>
                        <span className="ach-v">{fmtP(pTick(v.refEsToken0 ? futura.g!.plan.lo : futura.g!.plan.hi))} (+{fmtN(subidaHasta(entrada!, v.refEsToken0 ? futura.g!.plan.lo : futura.g!.plan.hi), 1)}%)</span></div>
                    </div>
                    <div className="ach-err" style={{ color: "#f2d28e", background: "rgba(232,183,106,.08)", borderColor: "rgba(232,183,106,.3)" }}>
                      {t("Hasta que llegue, tu piscina no cambia. Al ejecutarse recompras {a}: si después cae más de un {c}%, sales del rango por abajo con todo en {a}.", { a: activo.symbol, c: fmtN(futura.g!.plan.colchon, 1) })}
                    </div>
                    <p className="ach-sub" style={{ fontSize: 12 }}>
                      {orden ? t("Reemplaza la orden que tienes ahora. ") : ""}
                      {t("Son dos firmas: programar y dejar tu forma de rango de siempre. Cuesta una unidad mínima de {s} (una marca, no un aporte). Puedes cancelarla cuando quieras.", { s: estable.symbol })}
                    </p>
                  </>
                ) : !plan ? (
                  <div className="ach-err" style={{ color: "#cfcfd8", background: "#16161c", borderColor: "#2b2b34" }}>
                    {logicaOrden
                      ? t("Todavía no compensa redistribuir ahora. Elige un ancho en los precios gatillo para programarlo: el robot lo hará solo cuando {a} llegue a ese precio.", { a: activo.symbol })
                      : t("Todavía no compensa: {a} tiene que subir más para que tu piscina pueda estrecharse sola. Mira los precios gatillo.", { a: activo.symbol })}
                  </div>
                ) : (
                  <>
                    <div className="ach-card">
                      <div className="ach-big">{fmtN(plan.ancho * 100, 1)}%<span className="ach-unit">{t("de ancho")}</span>
                        <span className="ach-tag">{t("sin aportar")}</span></div>
                      <div className="ach-row"><span className="ach-k">{t("Sales con (en el techo)")}</span>
                        <span className="ach-v ach-ok">{fmtN(entrada!.T)} {estable.symbol}</span></div>
                      <div style={{ fontSize: 11.5, color: "#6f6f7c", marginTop: -2 }}>{t("Tu capital. Lo asegura el contrato.")}</div>
                      <div className="ach-row"><span className="ach-k">{t("Techo nuevo (aprox.)")}</span>
                        <span className="ach-v">{fmtP(pTick(v.refEsToken0 ? plan.lo : plan.hi))} (+{fmtN(plan.subida, 1)}%)</span></div>
                      <div className="ach-row"><span className="ach-k">{t("Abres con")}</span>
                        <span className="ach-v">{fmtN((1 - plan.fracEstable) * 100, 1)}% {activo.symbol} · {fmtN(plan.fracEstable * 100, 1)}% {estable.symbol}</span></div>
                    </div>
                    <div className="ach-err" style={{ color: "#f2d28e", background: "rgba(232,183,106,.08)", borderColor: "rgba(232,183,106,.3)" }}>
                      {t("El costo: recompras el {a} que el rango ya había vendido. Si {a} cae más de un {c}%, sales del rango por abajo con todo en {a}.", { a: activo.symbol, c: fmtN(plan.colchon, 1) })}
                      {costo && <> {t("Si cae un {p}% tendrías {con} {s} en vez de {sin} {s}.", { p: CAIDA, con: fmtN(costo.con), sin: fmtN(costo.sin), s: estable.symbol })}</>}
                    </div>
                    <p className="ach-sub" style={{ fontSize: 12 }}>{t("Son dos firmas: redistribuir y dejar tu forma de rango de siempre para cuando recuperes. No aportas nada de tu wallet.")}</p>
                  </>
                )}
                <div className="ach-foot">
                  <button className="ach-btn" onClick={cerrar}>{t("Cancelar")}</button>
                  <button className="ach-btn pri" onClick={firmar} disabled={ocupado || (!plan && !futura)}>
                    {acciones.haciendo === "redistribuir" ? <><span className="ach-spin" /> {t("Redistribuyendo…")}</>
                      : acciones.haciendo === "programarRedistribucion" ? <><span className="ach-spin" /> {t("Programando…")}</>
                      : futura ? t("Programar") : t("Redistribuir ahora")}
                  </button>
                </div>
              </div></div>
            )}
        </div>
      </div>
    </div>
  );
}
